import React from 'react';
import {
  REPORT_TYPES,
  QUARTERS,
  SEMESTERS,
  REPORT_TYPE_LABELS,
  schoolYearOptions,
  millisFromTimestamp,
  sortDraftsByGeneratedDesc,
  findMatchingDraft,
  formatPeriodLabel,
} from '../utils/narrativeDrafts';

/**
 * Class-level overview of report-card narrative status for the selected period.
 *
 * Read / display + open + print ONLY. One row per student in the visible
 * class(es): whether a saved draft exists for the chosen report type + period,
 * when it was last touched, and links to View / Print / Generate. Status is read
 * from the existing `students/{id}/narrativeDrafts` data model (passed in via
 * `draftsByStudent`); this component performs no Firestore reads itself.
 *
 * ── FUTURE BATCH-GENERATE MOUNT POINT ──────────────────────────────────────
 * This is the intended home for the deferred "class-level batch / background
 * report-card generation" feature (BACKLOG.md → "AI narrative — class-level
 * batch / background report-card generation"). That work should ADD a
 * "Generate for whole class" action onto this same overview (e.g. a header
 * button writing a `narrativeBatchRequests` doc + per-student status badges),
 * NOT replace it. The per-student data model and status resolution already live
 * here so batch output plugs into this listing rather than re-inventing one.
 * Do NOT add generation orchestration here until that item is scheduled.
 * ───────────────────────────────────────────────────────────────────────────
 */
function SavedReportCardsSection({
  expanded,
  onToggle,
  reportType,
  setReportType,
  quarterNumber,
  setQuarterNumber,
  semester,
  setSemester,
  schoolYear,
  setSchoolYear,
  classGroups,
  draftsByStudent,
  loading,
  onView,
  onPrint,
  onGenerate,
}) {
  const period = { reportType, schoolYear, quarterNumber, semester };
  const periodLabel = formatPeriodLabel(period);
  const reportTypeLabel = REPORT_TYPE_LABELS[reportType] || reportType;

  return (
    <div className="no-print" style={styles.wrap}>
      <button type="button" onClick={onToggle} style={styles.toggle} aria-expanded={expanded}>
        <span>📋 Saved Report Cards</span>
        <span style={styles.chevron}>{expanded ? '▾' : '▸'}</span>
      </button>

      {expanded && (
        <div style={styles.panel}>
          <p style={styles.help}>
            Report-card narrative status for every student in the selected class. Pick a report
            type and period to see who has a saved draft. View or print saved narratives, or
            generate one for a student who doesn't have it yet.
          </p>

          {/* Section-level period selector */}
          <div style={styles.controls}>
            <label style={styles.control}>
              <span style={styles.controlLabel}>Report type</span>
              <select style={styles.select} value={reportType} onChange={(e) => setReportType(e.target.value)}>
                {REPORT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>

            {reportType === 'quarterly' && (
              <label style={styles.control}>
                <span style={styles.controlLabel}>Quarter</span>
                <select style={styles.select} value={quarterNumber} onChange={(e) => setQuarterNumber(Number(e.target.value))}>
                  {QUARTERS.map((q) => <option key={q} value={q}>{`Q${q}`}</option>)}
                </select>
              </label>
            )}

            {reportType === 'semiannual' && (
              <label style={styles.control}>
                <span style={styles.controlLabel}>Semester</span>
                <select style={styles.select} value={semester} onChange={(e) => setSemester(e.target.value)}>
                  {SEMESTERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </label>
            )}

            <label style={styles.control}>
              <span style={styles.controlLabel}>School year</span>
              <select style={styles.select} value={schoolYear} onChange={(e) => setSchoolYear(e.target.value)}>
                {schoolYearOptions().map((sy) => <option key={sy} value={sy}>{sy}</option>)}
              </select>
            </label>
          </div>

          {loading && <p style={styles.muted}>Loading saved report cards…</p>}

          {!loading && classGroups.length === 0 && (
            <p style={styles.muted}>No students in the selected class.</p>
          )}

          {!loading && classGroups.map((group) => (
            <div key={group.classId} style={styles.classBlock}>
              <h4 style={styles.className}>Class: {group.originalClassId}</h4>
              <div style={styles.tableScroll}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Student</th>
                      <th style={styles.th}>Report Type</th>
                      <th style={styles.th}>Period</th>
                      <th style={styles.th}>Status</th>
                      <th style={styles.th}>Last edited</th>
                      <th style={styles.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.students.map((stu) => {
                      // Re-sort defensively so "most recent" holds regardless of
                      // the order drafts were cached in — same resolution the
                      // modal uses, so View opens exactly this draft.
                      const drafts = sortDraftsByGeneratedDesc(draftsByStudent[stu.studentId] || []);
                      const draft = findMatchingDraft(drafts, period);
                      const finalized = !!draft && draft.finalized === true;

                      let statusEl;
                      if (!draft) {
                        statusEl = <span style={styles.statusNone}>Not yet generated</span>;
                      } else if (finalized) {
                        statusEl = <span style={styles.statusSaved}>✓ Saved</span>;
                      } else {
                        statusEl = <span style={styles.statusDraft}>Draft (not saved)</span>;
                      }

                      const editedMillis = draft
                        ? millisFromTimestamp(draft.editedAt) || millisFromTimestamp(draft.generatedAt)
                        : 0;
                      const editedText = editedMillis ? new Date(editedMillis).toLocaleDateString() : '—';

                      return (
                        <tr key={stu.studentId} style={styles.tr}>
                          <td style={styles.td}>{stu.name}</td>
                          <td style={styles.td}>{reportTypeLabel}</td>
                          <td style={styles.td}>{periodLabel}</td>
                          <td style={styles.td}>{statusEl}</td>
                          <td style={styles.td}>{editedText}</td>
                          <td style={styles.td}>
                            {draft ? (
                              <div style={styles.actions}>
                                <button style={styles.viewBtn} onClick={() => onView(stu, draft)}>View</button>
                                <button style={styles.printBtn} onClick={() => onPrint(stu, draft, group.originalClassId)}>Print</button>
                              </div>
                            ) : (
                              <button style={styles.generateBtn} onClick={() => onGenerate(stu)}>Generate</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  wrap: { maxWidth: '980px', margin: '8px auto 28px', border: '1px solid #cbd5e1', borderRadius: '8px', background: '#f8fafc', overflow: 'hidden' },
  toggle: { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.05em', fontWeight: 700, color: '#1e293b' },
  chevron: { fontSize: '0.9em', color: '#64748b' },
  panel: { padding: '4px 16px 18px' },
  help: { fontSize: '0.86em', color: '#475569', margin: '0 0 12px' },
  controls: { display: 'flex', flexWrap: 'wrap', gap: '14px', marginBottom: '14px' },
  control: { display: 'flex', flexDirection: 'column', gap: '4px' },
  controlLabel: { fontSize: '0.78em', fontWeight: 600, color: '#334155' },
  select: { padding: '6px 8px', borderRadius: '5px', border: '1px solid #cbd5e1', fontSize: '0.9em', minWidth: '150px' },
  muted: { fontSize: '0.9em', color: '#64748b' },
  classBlock: { marginBottom: '18px' },
  className: { margin: '6px 0 8px', fontSize: '1em', color: '#0f172a' },
  tableScroll: { overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#fff' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.88em' },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e2e8f0', background: '#f1f5f9', color: '#334155', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #eef2f7' },
  td: { padding: '7px 10px', color: '#1f2937', verticalAlign: 'middle' },
  statusSaved: { color: '#15803d', fontWeight: 600 },
  statusDraft: { color: '#b45309', fontWeight: 600 },
  statusNone: { color: '#64748b' },
  actions: { display: 'flex', gap: '6px' },
  viewBtn: { padding: '3px 10px', fontSize: '0.85em', cursor: 'pointer', background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: '4px' },
  printBtn: { padding: '3px 10px', fontSize: '0.85em', cursor: 'pointer', background: '#e5e7eb', color: '#222', border: 'none', borderRadius: '4px' },
  generateBtn: { padding: '3px 10px', fontSize: '0.85em', cursor: 'pointer', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '4px' },
};

export default SavedReportCardsSection;
